import { cappedActionDelay, type DelayEntry } from '../sim/action-delay.js';
/**
 * How one of a skill's own statuses lives on one enemy: the price of everything the status makes possible (its hooks, the
 * effects bound to it, a sweep over its carriers) is read from this.
 *
 * The runtime keys an instance by (cast, status name, target), so every release of every stage that creates the status
 * lands on one timeline per target. Contacts carry their runtime roll identities: shared rolls are evaluated jointly,
 * and distinct rolls are independent (the representative target's geometry remains PROVISIONAL). A contact is
 * a point, or a chain: the pulses of one persistent area on one enemy, which are all there or none (the enemy stands in
 * the area or not). Every expectation below is exact over that sequence, by dynamic programming, under the runtime's own
 * rules (`sim/cast-statuses.ts`, `sim/status-definitions.ts`):
 *
 * - a contact that finds no live instance creates one (an episode); one that finds it refreshes it, and adds a stack when
 *   the status stacks;
 * - an instance with seconds of its own expires `lifetimeTicks` after its last contact, quantized as `createStatus` does
 *   (`statusDurationTicks`); a contact on the tick it would expire comes first and refreshes it;
 * - its timer keeps the schedule it started with, one firing per whole interval from the episode's first contact up to
 *   and including its expiry tick (`statusTickCount` for a single contact);
 * - an instance without seconds of its own outlives every contact of the cast.
 *
 * An episode is a maximal run of contacts each within a lifetime of the coverage so far, so it is the interval from its
 * first contact to its last contact's expiry; the DP carries the episodes that are still open, keyed by that interval.
 */
import { EvaluationEvidenceError } from './evidence-error.js';
import { monsterAttackDenied } from '../sim/action-denial.js';
import { elementLeafProvenance, elementPricedLeaves } from './element-value.js';
import { elementLifetimeS, requireElementPackage } from '../sim/element-package.js';
import type { LeafEffect, ElementStatusEffect } from '../types.js';
import { effectApplicationKey } from '../sim/effect-application-key.js';
import { statusDurationTicks, statusIntervalTicks } from '../sim/state.js';
import { PricingCaches } from './pricing-caches.js';

/** The status factory creates no instance for zero seconds; positive lifetimes use its whole-tick conversion. */
export function effectLifetimeTicks(durationS: number, tickRate: number): number {
  return durationS === 0 ? 0 : statusDurationTicks(durationS, tickRate);
}

/** A periodic effect's lifetime and interval on the evaluation host's whole-tick clock. */
export function periodicCadence(cadence: { readonly durationS: number; readonly tickS: number }, hostTickS?: number): { durationS: number; tickS: number } {
  if (hostTickS === undefined) return cadence;
  const rate = 1 / hostTickS;
  return { durationS: effectLifetimeTicks(cadence.durationS, rate) / rate, tickS: statusIntervalTicks(cadence.tickS, rate) / rate };
}

/** No model counts what this status does in this cast; the combination is refused rather than priced on a guess. */
export const SKILL_STATUS_COUNT_MODEL_UNAVAILABLE = 'SKILL_STATUS_COUNT_MODEL_UNAVAILABLE';

/** The most contacts one enemy's timeline may hold: the bound the delivery schedule already has. */
export const MAX_STATUS_APPLICATIONS = 1000;

/** One application to the representative enemy: when, and how likely it is a contact. */
export interface StatusContact {
  readonly order?: readonly number[];
  /** Ticks after the cast's release, ascending in the sequence. */
  readonly tick: number;
  /** The last tick of a chain of pulses that are all there or none; absent for a single contact. */
  readonly endTick?: number;
  readonly p: number;
  /** Contacts the chain makes when it is there (each adds a stack); absent means 1. */
  readonly count?: number;
  readonly gates?: readonly RuntimeRoll[];
  readonly rollKey?: string;
  readonly lastRollTick?: number;
  readonly source?: string;
  /** Contacts in the post-lifecycle phase observe exact-tick expiry first. */
  readonly expiryBeforeContact?: boolean;
  readonly lifetimeTicks?: number;
}

export interface StatusLife {
  /** Ticks an instance outlives its last contact; `null` when it outlives every contact of the cast. */
  readonly lifetimeTicks: number | null;
  /** Present when each contact adds a stack; `max` bounds them. Absent: a contact only refreshes. */
  readonly stacks?: { readonly max?: number };
  /** The timer's interval, for a status that has one. */
  readonly timerIntervalTicks?: number;
}

/** An instance the enemy gets, from its first contact to its expiry (`Infinity` for one that outlives every contact), and the chance of exactly that. */
export interface StatusEpisode {
  readonly startTick: number;
  readonly endTick: number;
  readonly probability: number;
  readonly stacks?: number;
  readonly since?: number;
  readonly source?: string;
  readonly lifetimeTicks?: number;
}

export interface StatusTimeline {
  /** The chance the enemy is struck at all over the cast. */
  readonly reached: number;
  /** Expected contacts over the cast. */
  readonly contacts: number;
  /** Expected instances the enemy gets over the cast: one per contact that finds none live. */
  readonly episodes: number;
  /** Expected firings of the timer over those instances; absent without a timer or without seconds of its own. */
  readonly timerTicks?: number;
  /** Expected stacks of the instance after the last contact; present only when the instance outlives every contact. */
  readonly stacksAfterLast?: number;
  /** The instances the enemy can get, each with its chance: where hooks fire and what a bound effect finds. */
  readonly instances: readonly StatusEpisode[];
  readonly spans?: readonly StatusEpisode[];
}

function assertSequence(contacts: readonly StatusContact[], applications: number): void {
  if (applications > MAX_STATUS_APPLICATIONS) throw new EvaluationEvidenceError(`${SKILL_STATUS_COUNT_MODEL_UNAVAILABLE}: more than ${MAX_STATUS_APPLICATIONS} applications`);
  for (let i = 0; i < contacts.length; i += 1) {
    const { tick, endTick, p } = contacts[i]!;
    if (!Number.isInteger(tick) || tick < 0 || !(p >= 0 && p <= 1)) throw new Error('status timeline: a contact has a whole non-negative tick and a probability');
    if (endTick !== undefined && !(Number.isInteger(endTick) && endTick >= tick)) throw new Error('status timeline: a chain ends on a whole tick, not before it starts');
    if (i > 0 && tick < contacts[i - 1]!.tick) throw new Error('status timeline: contacts are in tick order');
  }
}

/** E[min(N, max)] for N the contacts that land, each element its own Bernoulli worth `count` contacts. */
function expectedCappedCount(contacts: readonly StatusContact[], max: number | undefined): number {
  if (max === undefined) return contacts.reduce((sum, contact) => sum + contact.p * (contact.count ?? 1), 0);
  // P(N = k) for k < max, and the mass at max or above in the last cell.
  const mass = new Array<number>(max + 1).fill(0);
  mass[0] = 1;
  for (const { p, count = 1 } of contacts) {
    const next = new Array<number>(max + 1).fill(0);
    for (let k = 0; k <= max; k += 1) {
      next[k]! += mass[k]! * (1 - p);
      next[Math.min(max, k + count)]! += mass[k]! * p;
    }
    for (let k = 0; k <= max; k += 1) mass[k] = next[k]!;
  }
  return mass.reduce((sum, probability, k) => sum + probability * k, 0);
}

/**
 * The expectations of one enemy's timeline: episodes, timer firings and stacks after the last contact.
 *
 * Contacts are taken in start order. The open episodes are `(first contact, expiry)` pairs with their chance; a contact
 * joins every open episode (it starts within one) and extends it to its own expiry when that is later, else it starts a
 * new one. An open episode whose expiry is before the next contact can no longer be joined, so it is closed (counted)
 * the moment a later contact shows it.
 */
export function statusTimeline(contacts: readonly StatusContact[], life: StatusLife): StatusTimeline {
  assertSequence(contacts, contacts.reduce((sum, contact) => sum + (contact.count ?? 1), 0));
  const lifetime = life.lifetimeTicks;
  if (lifetime !== null && !(Number.isInteger(lifetime) && lifetime >= 1)) throw new Error('status timeline: a lifetime is a whole number of ticks');
  const interval = life.timerIntervalTicks;
  if (interval !== undefined && !(Number.isInteger(interval) && interval >= 1)) throw new Error('status timeline: a timer interval is a whole number of ticks');
  return timelineEpisodes(contacts, life);
}

/**
 * The episode owner, in either tick or second coordinates. Staged parents also retain their last source and periodic
 * phase; equal phases have identical future ticks, so they can share a state without retaining every first-contact time.
 * Their payload contacts follow lifecycle expiry, while skill-status contacts retain the status timeline's boundary rule.
 */
/** A window `[startS, endS)` in which the target cannot act; it holds with probability `p`, and one window is one shared roll for every pulse it covers. */
export interface ActionLockWindow { readonly startS: number; readonly endS: number; readonly p: number; readonly rollKey?: string }
export interface PeriodicDelayPolicy { readonly tickRate: number; readonly delayTicksBySource: Readonly<Record<string, number>>; readonly defaultDelayTicks: number; readonly locks?: readonly ActionLockWindow[] }
export interface PricedDelayIncrement { readonly source?: string; readonly previousS: number; readonly nextS: number; readonly probability: number }

/** The episode owner, on the supplied timeline clock. */
function timelineEpisodes(contacts: readonly StatusContact[], life: StatusLife, retainClock = true, stageCount?: number, keepSource = false, phaseInterval?: number, delayPolicy?: PeriodicDelayPolicy): StatusTimeline & { delayIncrements?: readonly PricedDelayIncrement[] } {
  if (stageCount !== undefined) return correlatedEpisodes(contacts, life, stageCount, keepSource, phaseInterval, delayPolicy);
  if (contacts.some((contact) => contact.gates?.some((gate) => gate.expression))) return correlatedEpisodes(contacts, life);
  const uses = new Map<string, number>();
  for (const contact of contacts) for (const gate of contact.gates ?? []) uses.set(gate.id, (uses.get(gate.id) ?? 0) + 1);
  contacts = contacts.map((contact) => {
    let p = contact.p;
    const gates = (contact.gates ?? []).filter((gate) => {
      if (gate.p > 0 && gate.p < 1 && uses.get(gate.id)! > 1) return true;
      p *= gate.outcome === false ? 1 - gate.p : gate.p;
      return false;
    });
    return { ...contact, p, gates };
  });
  if (contacts.some((contact) => contact.gates!.length > 0)) return correlatedEpisodes(contacts, life);
  const maxStacks = life.stacks === undefined ? 1 : life.stacks.max;
  const reached = 1 - contacts.reduce((product, contact) => product * (1 - contact.p), 1);
  const expectedContacts = contacts.reduce((sum, contact) => sum + contact.p * (contact.count ?? 1), 0);
  const lifetime = life.lifetimeTicks;
  const interval = life.timerIntervalTicks;
  const reach = lifetime ?? Number.POSITIVE_INFINITY;

  const instances: StatusEpisode[] = [];
  const spans = new Map<string, StatusEpisode>();
  const recordSpan = (span: StatusEpisode): void => {
    if (span.endTick <= span.startTick) return;
    const key = `${span.stacks}|${span.source}|${span.startTick}|${span.endTick}|${span.since}`;
    spans.set(key, { ...span, probability: span.probability + (spans.get(key)?.probability ?? 0) });
  };
  interface State extends StatusEpisode { readonly phase?: number; readonly rolls: Readonly<Record<string, boolean>> }
  let open: State[] = [{ startTick: -1, endTick: -1, probability: 1, stacks: 0, since: -1, rolls: {} }];
  const close = (state: State): void => {
    if (state.startTick >= 0) instances.push({ startTick: state.startTick, endTick: state.endTick, probability: state.probability,
      ...(stageCount === undefined ? {} : { stacks: state.stacks, source: state.source }) });
  };
  for (const contact of contacts) {
    const next = new Map<string, State>();
    const add = (state: State): void => {
      if (state.probability === 0) return;
      const key = `${phaseInterval !== undefined ? state.phase : retainClock ? state.startTick : state.startTick < 0 ? -1 : ''}|${state.endTick}`
        + (stageCount === undefined ? '' : `|${state.stacks}|${state.since}|${state.source}|${state.lifetimeTicks}|${JSON.stringify(state.rolls)}`);
      const held = next.get(key);
      const combined = (held?.probability ?? 0) + state.probability;
      const startTick = (!retainClock || phaseInterval !== undefined) && held !== undefined
        ? (held.startTick * held.probability + state.startTick * state.probability) / combined : state.startTick;
      next.set(key, { ...state, startTick, probability: combined });
    };
    for (let state of open) {
      if (stageCount !== undefined && state.startTick >= 0) recordSpan({ ...state, startTick: state.since!,
        endTick: Math.min(contact.tick, state.endTick), since: state.phase ?? state.startTick });
      if (state.startTick >= 0 && (state.endTick < contact.tick || (state.endTick === contact.tick && contact.expiryBeforeContact === true))) {
        close(state);
        state = { ...state, startTick: -1, endTick: -1, stacks: 0, phase: undefined, source: undefined, lifetimeTicks: undefined };
      }
      const known = contact.rollKey === undefined ? undefined : state.rolls[contact.rollKey];
      const chance = known === undefined ? contact.p : known ? 1 : 0;
      for (const hit of [false, true]) {
        const probability = state.probability * (hit ? chance : 1 - chance);
        if (probability === 0) continue;
        const rolls = { ...state.rolls };
        if (contact.rollKey !== undefined) {
          if (contact.tick === contact.lastRollTick) delete rolls[contact.rollKey]; else rolls[contact.rollKey] = hit;
        }
        const starts = state.startTick < 0;
        add({ ...state, probability, rolls, since: contact.tick,
          ...(hit ? { startTick: starts ? contact.tick : state.startTick,
            endTick: Math.max(state.endTick, (contact.endTick ?? contact.tick) + (!starts && keepSource ? state.lifetimeTicks ?? reach : contact.lifetimeTicks ?? reach)),
            source: !starts && keepSource ? state.source : contact.source,
            phase: starts && phaseInterval !== undefined ? contact.tick % phaseInterval : state.phase,
            lifetimeTicks: !starts && keepSource ? state.lifetimeTicks : contact.lifetimeTicks,
            stacks: Math.min(stageCount ?? 1, (starts ? 0 : state.stacks ?? 1) + (contact.count ?? 1)) } : {}) });
      }
    }
    open = [...next.values()];
  }
  for (const state of open) {
    close(state);
    if (stageCount !== undefined && state.startTick >= 0) recordSpan({ ...state, startTick: state.since!, since: state.phase ?? state.startTick });
  }

  const episodes = instances.reduce((sum, episode) => sum + episode.probability, 0);
  const timerTicks = interval === undefined || lifetime === null
    ? undefined
    : instances.reduce((sum, episode) => sum + episode.probability * Math.floor((episode.endTick - episode.startTick) / interval), 0);
  return {
    reached, contacts: expectedContacts, episodes, instances,
    ...(stageCount === undefined ? {} : { spans: [...spans.values()] }),
    ...(timerTicks === undefined ? {} : { timerTicks }),
    ...(lifetime === null ? { stacksAfterLast: expectedCappedCount(contacts, maxStacks) } : {}),
  };
}

/** A runtime observation or draw is named by its snapshot or draw identity; p is its ordered threshold. */
export interface RuntimeRoll { readonly id: string; readonly p: number; readonly outcome?: boolean; readonly expression?: { readonly and?: readonly RuntimeRoll[]; readonly or?: readonly RuntimeRoll[]; readonly not?: RuntimeRoll } }

/** Joint probability of constraints on the same state observation or random draw. */
export function runtimeGateProbability(gates: readonly RuntimeRoll[]): number {
  if (gates.every((gate) => gate.expression === undefined)) {
    const ranges = new Map<string, { low: number; high: number }>();
    for (const gate of gates) {
      const range = ranges.get(gate.id) ?? { low: 0, high: 1 };
      if (gate.outcome === false) range.low = Math.max(range.low, gate.p);
      else range.high = Math.min(range.high, gate.p);
      ranges.set(gate.id, range);
    }
    return [...ranges.values()].reduce((p, range) => p * Math.max(0, range.high - range.low), 1);
  }
  return runtimeRollExpectation<boolean>([{ p: 1, gates }], false, (_, _i, pass) => pass, String)
    .reduce((sum, row) => sum + (row.state ? row.probability : 0), 0);
}

/** Rebase every observed variable when an application enters a target/release namespace. */
export function mapRuntimeRollIds(gate: RuntimeRoll, map: (id: string) => string): RuntimeRoll {
  const child = (g: RuntimeRoll) => mapRuntimeRollIds(g, map);
  const e = gate.expression;
  return { ...gate, id: map(gate.id), ...(e ? { expression: e.not ? { not: child(e.not) }
    : e.and ? { and: e.and.map(child) } : { or: e.or!.map(child) } } : {}) };
}

function observations(gate: RuntimeRoll): readonly RuntimeRoll[] {
  const e = gate.expression;
  return e ? (e.not ? observations(e.not) : (e.and ?? e.or ?? []).flatMap(observations)) : [gate];
}

function gateHolds(gate: RuntimeRoll, values: ReadonlyMap<string, number>): boolean {
  const e = gate.expression;
  const holds = e ? (e.not ? !gateHolds(e.not, values) : e.and ? e.and.every((g) => gateHolds(g, values))
    : e.or!.some((g) => gateHolds(g, values))) : values.get(gate.id)! < gate.p;
  return holds === (gate.outcome ?? true);
}

/**
 * Exact expectation over runtime rolls. A roll is retained until its last dependent event; unrelated rolls are
 * independent. Both the live observation partitions and merged timeline states use the existing 1000-entry pricing
 * budget. Exhausting it is an evidence refusal, never a marginal approximation.
 */
export function runtimeRollExpectation<T>(
  events: readonly { readonly p: number; readonly gates?: readonly RuntimeRoll[] }[],
  initial: T,
  transition: (state: T, index: number, passes: boolean, probability: number) => T,
  keyOf: (state: T) => string,
): readonly { readonly state: T; readonly probability: number }[] {
  const last = new Map<string, number>();
  const probabilities = new Map<string, number[]>();
  events.forEach((event, i) => event.gates?.flatMap(observations).forEach((roll) => {
    const thresholds = probabilities.get(roll.id) ?? [0, 1];
    if (!thresholds.includes(roll.p)) thresholds.push(roll.p);
    probabilities.set(roll.id, thresholds.sort((a, b) => a - b));
    last.set(roll.id, i);
  }));
  let states = [{ state: initial, probability: 1, rolls: new Map<string, number>() }];
  const refuse = (): never => { throw new EvaluationEvidenceError(`${SKILL_STATUS_COUNT_MODEL_UNAVAILABLE}: joint roll evaluation exceeds ${MAX_STATUS_APPLICATIONS} outcomes/states`); };
  events.forEach((event, i) => {
    const next = new Map<string, typeof states[number]>();
    for (const state of states) {
      let branches = [state];
      for (const roll of event.gates?.flatMap(observations) ?? []) {
        branches = branches.flatMap((branch) => branch.rolls.has(roll.id) ? [branch]
          : probabilities.get(roll.id)!.slice(1).map((high, index) => {
            const low = probabilities.get(roll.id)![index]!;
            return { ...branch, probability: branch.probability * (high - low),
              rolls: new Map([...branch.rolls, [roll.id, (low + high) / 2]]) };
          }).filter((branch) => branch.probability > 0));
        if (branches.length > MAX_STATUS_APPLICATIONS) refuse();
      }
      for (const branch of branches) {
        const p = (event.gates ?? []).every((roll) => gateHolds(roll, branch.rolls)) ? event.p : 0;
        for (const pass of [false, true]) {
          const probability = branch.probability * (pass ? p : 1 - p);
          if (probability === 0) continue;
          const value = transition(branch.state, i, pass, probability);
          const rolls = new Map([...branch.rolls].filter(([id]) => last.get(id)! > i));
          const key = JSON.stringify([keyOf(value), [...rolls]]);
          const held = next.get(key);
          if (held) held.probability += probability;
          else next.set(key, { state: value, probability, rolls });
        }
      }
    }
    if (next.size > MAX_STATUS_APPLICATIONS) refuse();
    states = [...next.values()];
  });
  return states;
}

/** Every timeline reader uses the same joint roll owner. */
function correlatedEpisodes(contacts: readonly StatusContact[], life: StatusLife, stageCount?: number, keepSource = false, phaseInterval?: number, delayPolicy?: PeriodicDelayPolicy): StatusTimeline & { delayIncrements: readonly PricedDelayIncrement[] } {
  const instances: StatusEpisode[] = [];
  const spans: StatusEpisode[] = [];
  const delayIncrements: PricedDelayIncrement[] = [];
  const advance = (state: { end: number; since: number; phase: number; source: string | undefined; ledger: readonly DelayEntry[]; total: number; locks: readonly boolean[] }, end: number, probability: number) => {
    let { ledger, total } = state;
    if (delayPolicy !== undefined && phaseInterval !== undefined && state.end !== -Infinity) {
      const first = Math.floor((state.since - state.phase) / phaseInterval) + 1;
      const last = Math.floor((end - state.phase) / phaseInterval);
      for (let k = first; k <= last; k++) {
        if ((delayPolicy.locks ?? []).some((lock, i) => state.locks[i] && lock.startS * delayPolicy.tickRate <= state.phase + k * phaseInterval && state.phase + k * phaseInterval < lock.endS * delayPolicy.tickRate)) continue;
        const requested = (state.source === undefined ? undefined : delayPolicy.delayTicksBySource[state.source]) ?? delayPolicy.defaultDelayTicks;
        const result = cappedActionDelay(ledger, state.phase + k * phaseInterval, requested, delayPolicy.tickRate);
        ledger = result.entries;
        const next = total + result.applied / delayPolicy.tickRate;
        if (next > total) delayIncrements.push({ source: state.source, previousS: total, nextS: next, probability });
        total = next;
      }
    }
    return { ledger, total };
  };
  let expectedContacts = 0;
  const initial = { start: 0, end: -Infinity, reached: false, stacks: 0, since: 0, phase: 0, source: undefined as string | undefined, lifetime: undefined as number | undefined, ledger: [] as readonly DelayEntry[], total: 0, locks: [] as readonly boolean[] };
  const lockEvents = (delayPolicy?.locks ?? []).map((lock, i) => ({ p: 1, gates: [{ id: lock.rollKey ?? `delay-lock/${i}`, p: lock.p }] }));
  const states = runtimeRollExpectation([...lockEvents, ...contacts], initial, (state, i, pass, probability) => {
    if (i < lockEvents.length) return { ...state, locks: [...state.locks, pass] };
    const contact = contacts[i - lockEvents.length]!;
    let { start, end, stacks, since, phase, source, lifetime } = state;
    let { ledger, total } = advance(state, Math.min(contact.tick, end), probability);
    if (stageCount !== undefined && end !== -Infinity && Math.min(contact.tick, end) > since)
      spans.push({ startTick: since, endTick: Math.min(contact.tick, end), since: phase, stacks, source, probability });
    if (end !== -Infinity && (end < contact.tick || (end === contact.tick && contact.expiryBeforeContact))) {
      instances.push({ startTick: start, endTick: end, probability, stacks, source });
      end = -Infinity; stacks = 0; source = undefined; lifetime = undefined; total = 0;
    }
    if (pass) {
      expectedContacts += probability * (contact.count ?? 1);
      const starts = end === -Infinity;
      if (starts) { start = contact.tick; phase = phaseInterval === undefined ? start : start % phaseInterval; }
      if (starts || !keepSource) { source = contact.source; lifetime = contact.lifetimeTicks; }
      end = Math.max(end, (contact.endTick ?? contact.tick) + (lifetime ?? life.lifetimeTicks ?? Infinity));
      stacks = Math.min(stageCount ?? (life.stacks === undefined ? 1 : life.stacks.max ?? Infinity), stacks + (contact.count ?? 1));
    }
    return { start, end, stacks, since: contact.tick, phase, source, lifetime, ledger, total, locks: state.locks, reached: state.reached || pass };
  }, (state) => JSON.stringify(state));
  for (const { state, probability } of states) if (state.end !== -Infinity) {
    advance(state, state.end, probability);
    instances.push({ startTick: state.start, endTick: state.end, probability, stacks: state.stacks, source: state.source });
    if (stageCount !== undefined && state.end > state.since) spans.push({ startTick: state.since, endTick: state.end, since: state.phase, stacks: state.stacks, source: state.source, probability });
  }
  return { delayIncrements, reached: states.reduce((sum, { state, probability }) => sum + (state.reached ? probability : 0), 0), contacts: expectedContacts,
    episodes: instances.reduce((sum, episode) => sum + episode.probability, 0), instances, ...(stageCount === undefined ? {} : { spans }),
    ...(life.timerIntervalTicks === undefined || life.lifetimeTicks === null ? {} : { timerTicks: instances.reduce((sum, episode) => sum + episode.probability * Math.floor((episode.endTick - episode.startTick) / life.timerIntervalTicks!), 0) }),
    ...(life.lifetimeTicks === null ? { stacksAfterLast: states.reduce((sum, { state, probability }) => sum + state.stacks * probability, 0) } : {}) };
}

/** The pulses of one unit on one enemy as runs: a gap longer than the lifetime ends an instance, so it ends a run. */
export function statusApplicationRuns(pulseTicks: readonly number[], lifetime: number | null): readonly { readonly first: number; readonly last: number; readonly count: number }[] {
  const runs: { first: number; last: number; count: number }[] = [];
  for (const tick of pulseTicks) {
    const run = runs[runs.length - 1];
    if (run !== undefined && (lifetime === null || tick - run.last <= lifetime)) { run.last = tick; run.count += 1; } else runs.push({ first: tick, last: tick, count: 1 });
  }
  return runs;
}

/** Every application of one authored effect to one target, in absolute cast seconds. Groups have disjoint targets. */
export interface EffectApplication {
  /** Stable victim identity when a history comes from a sampled field. */
  readonly targetKey?: string;
  /** Runtime release/unit identity, retained independently of its numeric probability. */
  readonly id?: string;
  readonly order?: readonly number[];
  readonly atS: number;
  readonly share: number;
  /** Pulses of one delivery: this target is present for all of them or none. */
  readonly offsetsS?: readonly number[];
  readonly gates?: readonly RuntimeRoll[];
  readonly source?: string;
  readonly expiryBeforeContact?: boolean;
  readonly durationS?: number;
}
export type EffectApplications = readonly (readonly EffectApplication[])[];
export type MaintainedApplications = readonly number[] | EffectApplications;

/** A supplied host owns the clock. Clockless analytic callers use an exact binary lattice, never float comparisons. */
function timelineRate(hostTickS: number | undefined, coordinates: readonly number[]): number {
  if (hostTickS !== undefined) return 1 / hostTickS;
  let rate = 1;
  while (coordinates.some((coordinate) => Number.isFinite(coordinate) && !Number.isInteger(coordinate * rate))) {
    rate *= 2;
    if (!Number.isFinite(rate) || coordinates.some((coordinate) => Number.isFinite(coordinate) && !Number.isFinite(coordinate * rate))) {
      throw new EvaluationEvidenceError(`${SKILL_STATUS_COUNT_MODEL_UNAVAILABLE}: timeline coordinates exceed the exact clock`);
    }
  }
  return rate;
}

function applicationCoordinates(groups: EffectApplications): number[] {
  return groups.flatMap((group) => group.flatMap((application) => (application.offsetsS ?? [0]).map((offset) => application.atS + offset)));
}

function compareApplicationOrder(a: EffectApplication, b: EffectApplication): number {
  for (let i = 0; i < Math.max(a.order?.length ?? 0, b.order?.length ?? 0); i++) {
    const difference = (a.order?.[i] ?? 0) - (b.order?.[i] ?? 0);
    if (difference !== 0) return difference;
  }
  return 0;
}

/** Applications on a target in the runtime's tick, attack-unit, then effect order. */
export function orderedEffectApplications(group: readonly EffectApplication[], tickS?: number): EffectApplication[] {
  const rate = timelineRate(tickS, applicationCoordinates([group]));
  const tick = (seconds: number): number => Math.round(seconds * rate);
  return group.flatMap((application, index) => (application.offsetsS ?? [0]).map((offset) => ({ ...application, atS: application.atS + offset, offsetsS: undefined,
    ...(application.offsetsS === undefined || application.share >= 1 ? {} : { share: 1, gates: [...(application.gates ?? []), { id: `schedule/${index}`, p: application.share }] }),
  })))
    .sort((a, b) => {
      const time = tick(a.atS) - tick(b.atS);
      if (time !== 0) return time;
      return compareApplicationOrder(a, b);
    });
}

/**
 * The whole cast's refreshed DoT ticks and strongest-wins active time. This reads the same episode owner as skill
 * statuses; fractional contacts are Bernoulli applications, never certain refreshes. Values above one are whole
 * applications plus a fractional one. Equal authored magnitudes share one timeline per target and authored effect.
 */
type EffectTimeline = { readonly contacts: number; readonly activeS: number; readonly ticks: readonly { readonly atS: number; readonly share: number }[] };
type StagedEffectTimeline = EffectTimeline & {
  readonly delayIncrements: readonly PricedDelayIncrement[];
  readonly spans: readonly StatusEpisode[];
  readonly stageTicks: readonly { readonly stage: number; readonly source?: string; readonly atS: number; readonly share: number }[];
  readonly stages: readonly { readonly stage: number; readonly activeS: number; readonly ticks: number; readonly episodes: number; readonly source?: string }[];
};
export function effectApplicationTimeline(groups: EffectApplications, durationS: number, tickS: number | undefined, hostTickS: number | undefined, stageCount: number, keepSource?: boolean, delayPolicy?: PeriodicDelayPolicy): StagedEffectTimeline;
export function effectApplicationTimeline<T extends number | undefined = undefined>(groups: EffectApplications, durationS: number, tickS?: number, hostTickS?: T): T extends 2 | 3 | 4 | 5 ? StagedEffectTimeline : EffectTimeline;
export function effectApplicationTimeline(groups: EffectApplications, durationS: number, tickS?: number, hostTickS?: number, stageCount?: number, keepSource = false, delayPolicy?: PeriodicDelayPolicy): EffectTimeline {
  return computeEffectApplicationTimeline(groups, durationS, tickS, hostTickS, stageCount, keepSource, delayPolicy);
}

function computeEffectApplicationTimeline(groups: EffectApplications, durationS: number, tickS?: number, hostTickS?: number, stageCount?: number, keepSource = false, delayPolicy?: PeriodicDelayPolicy): EffectTimeline {
  if (stageCount === undefined && hostTickS !== undefined && Number.isInteger(hostTickS) && hostTickS > 1) { stageCount = hostTickS; hostTickS = undefined; }
  // On a host clock the timeline is kept in the runtime's whole ticks: every comparison of application, refresh, expiry
  // and pulse is an integer one, never a float-seconds equality. Seconds are only the unit it is read back in.
  const rate = timelineRate(hostTickS, [...applicationCoordinates(groups), durationS, ...(tickS === undefined ? [] : [tickS])]);
  const toSeconds = (units: number): number => units / rate;
  const toUnit = (seconds: number): number => Math.round(seconds * rate);
  const lifetime = hostTickS === undefined ? toUnit(durationS) : effectLifetimeTicks(durationS, rate);
  const interval = tickS === undefined ? undefined : hostTickS === undefined ? toUnit(tickS) : statusIntervalTicks(tickS, rate);
  const delayIncrements: PricedDelayIncrement[] = [];
  let contacts = 0;
  let activeS = 0;
  const stages: { stage: number; activeS: number; ticks: number; episodes: number; source?: string }[] = Array.from({ length: stageCount ?? 0 }, (_, i) => ({ stage: i + 1, activeS: 0, ticks: 0, episodes: 0 }));
  const ticks = new Map<number, number>();
  const spans = new Map<string, StatusEpisode>();
  const stageTicks = new Map<string, { stage: number; source?: string; atS: number; share: number }>();
  for (const group of groups) {
    const sequence = orderedEffectApplications(group, hostTickS).flatMap(({ atS, share, offsetsS = [0], gates, source, durationS: applicationDurationS, expiryBeforeContact }): StatusContact[] => {
      const whole = Math.floor(share);
      const probabilities = [...Array.from({ length: whole }, () => 1), ...(share > whole ? [share - whole] : [])];
      const pulses = offsetsS.map((offset) => toUnit(atS + offset));
      return probabilities.flatMap((p) => statusApplicationRuns(pulses, lifetime).map((run) => ({
        tick: run.first, endTick: run.last, p, count: run.count, gates, source, expiryBeforeContact, lifetimeTicks: applicationDurationS === undefined ? undefined : (hostTickS === undefined ? toUnit(applicationDurationS) : effectLifetimeTicks(applicationDurationS, rate)),
      })));
    }).sort((a, b) => a.tick - b.tick);
    const timeline = timelineEpisodes(sequence, { lifetimeTicks: lifetime }, interval !== undefined || stageCount !== undefined, stageCount, keepSource, interval, delayPolicy);
    delayIncrements.push(...(timeline.delayIncrements ?? []));
    const resultOf = (state: StatusEpisode) => {
      let result = stages.find((stage) => stage.stage === state.stacks && stage.source === state.source);
      if (result === undefined) { result = { stage: state.stacks!, activeS: 0, ticks: 0, episodes: 0, source: state.source }; stages.push(result); }
      return result;
    };
    for (const episode of timeline.instances) if (stageCount !== undefined) resultOf(episode).episodes += episode.probability;
    for (const span of timeline.spans ?? []) {
      const key = `${span.stacks}|${span.source}|${span.startTick}|${span.endTick}|${span.since}`;
      spans.set(key, { ...span, probability: span.probability + (spans.get(key)?.probability ?? 0) });
    }
    for (const span of timeline.spans ?? []) {
      const result = resultOf(span);
      result.activeS += toSeconds(span.endTick - span.startTick) * span.probability;
      if (interval !== undefined) {
        const first = Math.floor((span.startTick - span.since!) / interval);
        const last = Math.floor((span.endTick - span.since!) / interval);
        result.ticks += (last - first) * span.probability;
        for (let k = first + 1; k <= last; k++) {
          const atS = toSeconds(span.since! + k * interval);
          const key = JSON.stringify([span.stacks, span.source, atS]);
          stageTicks.set(key, { stage: span.stacks!, source: span.source, atS, share: span.probability + (stageTicks.get(key)?.share ?? 0) });
        }
      }
    }
    contacts += timeline.contacts;
    for (const episode of timeline.instances) {
      activeS += toSeconds(episode.endTick - episode.startTick) * episode.probability;
      if (interval !== undefined) {
        const count = Math.floor((episode.endTick - episode.startTick) / interval);
        for (let k = 1; k <= count; k++) {
          const at = episode.startTick + k * interval;
          ticks.set(at, (ticks.get(at) ?? 0) + episode.probability);
        }
      }
    }
  }
  const staged = { delayIncrements, stages, spans: [...spans.values()].map((span) => ({ ...span, startTick: toSeconds(span.startTick), endTick: toSeconds(span.endTick), since: span.since === undefined ? undefined : toSeconds(span.since) })), stageTicks: [...stageTicks.values()].sort((a, b) => a.atS - b.atS) };
  return { contacts, activeS, ...(stageCount === undefined ? {} : staged), ticks: [...ticks].sort(([a], [b]) => a - b).map(([at, share]) => ({ atS: toSeconds(at), share })) };
}

/** Normalize the legacy delivery-only offsets and the whole-cast schedule at the timeline boundary. */
export function maintainedEffectRatio(durationS: number, applications: MaintainedApplications = [0], hostTickS?: number): number {
  const groups: EffectApplications = applications.length === 0 || typeof applications[0] === 'number'
    ? [(applications as readonly number[]).map((atS) => ({ atS, share: 1 }))]
    : applications as EffectApplications;
  if (!(durationS > 0)) return 1;
  const timeline = effectApplicationTimeline(groups, durationS, undefined, hostTickS);
  const realized = hostTickS === undefined ? durationS : effectLifetimeTicks(durationS, 1 / hostTickS) * hostTickS;
  return timeline.contacts > 0 ? timeline.activeS / (timeline.contacts * realized) : 0;
}

/**
 * The enemies a release's contacts are spread over when each enemy is struck by each unit independently: the `M` for
 * which `M` enemies, each struck by unit `u` with probability `c_u / M`, are reached `reached` times in expectation and
 * contacted `Σ c_u` times. With it, `M` times a representative enemy's expectation is the release's total, and the two
 * totals the price already counts (the enemies a release reaches, its contacts) are reproduced exactly.
 *
 * `M(1 - Π(1 - c_u / M))` rises from the largest `c_u` (one unit certain to strike) towards `Σ c_u` (no two units on one
 * enemy), so it is found by bisection; a release whose units never share an enemy has no finite `M`, and is read at the
 * bound where the shared contacts are below the precision of the count.
 */
export function bernoulliPopulation(unitContacts: readonly number[], reached: number): number {
  const largest = Math.max(0, ...unitContacts);
  const reachedBy = (m: number): number => m * (1 - unitContacts.reduce((product, c) => product * (1 - c / m), 1));
  if (!(reached > largest)) return largest;
  let low = largest;
  let high = Math.max(1, largest) * 2;
  for (let doubling = 0; reachedBy(high) < reached && doubling < 60; doubling += 1) high *= 2;
  for (let step = 0; step < 200 && high - low > 1e-12 * high; step += 1) {
    const mid = (low + high) / 2;
    if (reachedBy(mid) < reached) low = mid; else high = mid;
  }
  return high;
}

/** A distribution of the ticks a parent status has left: `{ ticks, p }` pairs summing to one (a parent not live is `ticks: 0`). */
export type RemainingLifetime = readonly { readonly ticks: number; readonly p: number }[];

/**
 * What a parent created by the same contact has left when an effect bound to it is applied: all of its lifetime. A stage's
 * status effects are applied before its other effects (`sim/runtime.ts`), so the child always finds the parent the same
 * contact just created or refreshed.
 */
export function remainingAtOwnContact(lifetimeTicks: number): RemainingLifetime {
  return [{ ticks: lifetimeTicks, p: 1 }];
}

/**
 * What a parent has left at a tick, over the instances it can have there: an instance covering the tick has the rest of
 * its coverage (later contacts extend it, and a bound effect lives as long as the instance does), and no instance at all
 * is `ticks: 0`. A parent that outlives every contact has `Infinity`.
 */
export function remainingLifetimeAt(instances: readonly StatusEpisode[], tick: number): RemainingLifetime {
  const covering = instances.filter((episode) => episode.startTick <= tick && tick <= episode.endTick);
  const live = covering.reduce((sum, episode) => sum + episode.probability, 0);
  return [...covering.map((episode) => ({ ticks: episode.endTick - tick, p: episode.probability })), { ticks: 0, p: Math.max(0, 1 - live) }];
}

/** E[min(own, R)] in ticks over the parent's remaining lifetime R: how long a bound effect runs. */
export function expectedBoundTicks(ownTicks: number, remaining: RemainingLifetime): number {
  return remaining.reduce((sum, { ticks, p }) => sum + p * Math.min(ownTicks, ticks), 0);
}

/** The kinds whose seconds the runtime scales by the target's susceptibility before a binding cuts them (`sim/runtime.ts`). */
/** Binding cuts priced seconds, while refresh identity still reads the duration the runtime was authored with. */
const boundDotDurations = new WeakMap<object, number>();

export function pricedDotKey(node: { readonly kind: 'dot'; readonly damageType?: string; readonly tag?: string; readonly durationS: number; readonly tickS: number }, stageId?: string): string {
  return effectApplicationKey({ ...node, durationS: boundDotDurations.get(node) ?? node.durationS }, { stageId });
}

const HOLD_KINDS: ReadonlySet<string> = new Set(['stun', 'root', 'silence', 'disarm']);

export interface BoundPricingContext {
  /** The tick a status's seconds are quantized by; a bound effect has no price without it. */
  readonly tickS?: number;
  /** The susceptibility the price reads a hold at (`EvalContext.ccSusceptibility`). */
  readonly ccSusceptibility: number;
}

export type BoundEffectsResult<T> =
  | { readonly ok: true; readonly effects: readonly T[] }
  | { readonly ok: false; readonly code: string; readonly message: string };

/**
 * A stage's effects as their price reads them: an effect bound to a status (`while`) runs `E[min(own, R)]`, its own
 * realized seconds against what the parent has left when it is applied, and is priced at the seconds that leaves (a hold's
 * read back through the susceptibility the price scales it by). An effect at the top of the stage bound to a status the
 * same stage creates finds the parent the same contact created or refreshed (`remainingAtOwnContact`); one bound to a
 * status an earlier stage creates is read from the parent's timeline at the ticks this stage lands (`remaining`, which
 * `castStageHits` supplies per status). Both need the parent to have seconds of its own. Any other binding is refused by name.
 */
export function boundEffectsForPricing<T>(
  stageId: string,
  effects: readonly T[],
  /** Effects the stage binds elsewhere (a weapon contact's regions): a binding there has no model. */
  elsewhere: readonly unknown[],
  statuses: readonly { readonly name: string; readonly durationS?: number }[],
  ctx: BoundPricingContext,
  /** What each parent an earlier stage creates has left when this stage lands (`CastStageHits.boundRemaining`). */
  remaining: ReadonlyMap<string, RemainingLifetime> = new Map(),
): BoundEffectsResult<T> {
  const leafOf = (node: unknown): Record<string, unknown> => node as Record<string, unknown>;
  const created = new Set(effects.map(leafOf).filter((node) => node.kind === 'status').map((node) => String(node.name)));
  const nestedWhile = (nodes: unknown, nested: boolean): boolean => Array.isArray(nodes) && nodes.some((node) => {
    const record = leafOf(node);
    if (nested && typeof record.while === 'string') return true;
    return nestedWhile(record.children, true) || nestedWhile(record.effects, true);
  });
  if (nestedWhile(effects, false) || nestedWhile(elsewhere, true)) {
    return { ok: false, code: SKILL_STATUS_COUNT_MODEL_UNAVAILABLE, message: `stage "${stageId}" binds an effect to a status below the top of its effects; how long it runs has no model` };
  }
  if (!effects.some((node) => typeof leafOf(node).while === 'string')) return { ok: true, effects };
  const out: T[] = [];
  for (const node of effects) {
    const leaf = leafOf(node);
    if (typeof leaf.while !== 'string') { out.push(node); continue; }
    const parent = statuses.find((status) => status.name === leaf.while);
    if (parent === undefined || parent.durationS === undefined) {
      return { ok: false, code: SKILL_STATUS_COUNT_MODEL_UNAVAILABLE, message: `stage "${stageId}" binds a ${String(leaf.kind)} to status "${String(leaf.while)}", which has no seconds of its own; how long it runs has no model` };
    }
    const fromEarlier = created.has(parent.name) ? undefined : remaining.get(parent.name);
    if (!created.has(parent.name) && fromEarlier === undefined) {
      return { ok: false, code: SKILL_STATUS_COUNT_MODEL_UNAVAILABLE, message: `stage "${stageId}" binds a ${String(leaf.kind)} to status "${parent.name}", which an earlier stage creates and whose remaining lifetime when this stage lands has no model` };
    }
    if (ctx.tickS === undefined) {
      return { ok: false, code: SKILL_STATUS_COUNT_MODEL_UNAVAILABLE, message: `stage "${stageId}" binds an effect to status "${parent.name}", and this evaluation context supplies no tick to quantize its seconds by` };
    }
    const rate = 1 / ctx.tickS;
    const scale = HOLD_KINDS.has(String(leaf.kind)) ? ctx.ccSusceptibility : 1;
    const ownTicks = (leaf.durationS as number) * scale * rate;
    const ranTicks = expectedBoundTicks(ownTicks, fromEarlier ?? remainingAtOwnContact(statusDurationTicks(parent.durationS, rate)));
    const bounded = { ...leaf, durationS: ranTicks / rate / scale };
    if (leaf.kind === 'dot') boundDotDurations.set(bounded, leaf.durationS as number);
    out.push(bounded as T);
  }
  return { ok: true, effects: out };
}

/** A priced source of one maintained runtime key, before other leaves of the cast are overlapped. */
export interface KeyedMaintainedValue {
  readonly key: string;
  /** Runtime magnitude orders active instances; duration worth only sets their price rate. */
  readonly strength: number;
  readonly groups: EffectApplications;
  readonly durationS: number;
  readonly total: number;
  readonly tickS?: number;
  /** Alternative realized stages of one parent are mutually exclusive on the target. */
  readonly exclusive?: string;
  readonly variant?: string;
}

/**
 * Attribute the strongest-wins value to its sources over the whole cast. Each target bucket reads the same key across
 * stages, leaves and releases. Only explicit runtime roll identities correlate arrivals; equal probabilities or
 * timestamps carry no identity. The existing per-source value supplies the rate, including duration worth.
 */
export function keyedMaintainedValues(sources: readonly KeyedMaintainedValue[], structures?: Map<string, MaintainedStructure>): number[] {
  const tickS = sources.find((source) => source.tickS !== undefined)?.tickS;
  const structure = maintainedStructureOf(sources, tickS, structures);
  const { rate } = structure;
  const values = sources.map(() => 0);
  const compensation = sources.map(() => 0);
  const add = (index: number, amount: number): void => {
    const corrected = amount - compensation[index]!;
    const next = values[index]! + corrected;
    compensation[index] = (next - values[index]!) - corrected;
    values[index] = next;
  };
  for (const { indices, active, targets } of structure.keys) {
    if (indices.length === 1) { values[indices[0]!] = sources[indices[0]!]!.total; continue; }
    const rates = indices.map((i) => active.get(i)! > 0 ? sources[i]!.total / active.get(i)! : 0);
    const rateOf = new Map(indices.map((i, at) => [i, rates[at]!]));
    // The order sources claim an interval in does not depend on the interval.
    const order = [...indices].sort((a, b) => sources[b]!.strength - sources[a]!.strength || rateOf.get(b)! - rateOf.get(a)!);
    const orderKey = order.join(',');
    // Every interval of every target is one step for every source; a source an interval does not cover adds exactly zero in it,
    // which only changes it while its compensation is unsettled, so those steps are applied when it is next covered (or at the end).
    let steps = 0;
    const lastStep = indices.map(() => -1);
    const settle = (at: number, count: number): void => {
      const i = indices[at]!;
      for (let k = 0; k < count && compensation[i] !== 0; k++) {
        const before = values[i]!; const was = compensation[i]!;
        add(i, 0 * rates[at]!);
        if (values[i] === before && compensation[i] === was) break;
      }
    };
    for (const target of targets) {
      let claims = target.claims.get(orderKey);
      if (claims === undefined) {
        claims = claimsOf(target, order, indices, rate);
        if (target.claims.size >= MAINTAINED_CLAIMS_PER_TARGET) target.claims.clear();
        target.claims.set(orderKey, claims);
      }
      for (let b = 1; b < claims.length; b++, steps++) {
        for (const claim of claims[b]!) {
          settle(claim.at, steps - lastStep[claim.at]! - 1);
          lastStep[claim.at] = steps;
          add(claim.i, claim.spanS * rates[claim.at]! * claim.share);
        }
      }
    }
    for (let at = 0; at < indices.length; at++) settle(at, steps - lastStep[at]! - 1);
  }
  return values;
}

interface MaintainedApplication { readonly keyId: number; readonly live: readonly number[]; readonly p: number; readonly gates: EffectApplication['gates']; readonly exclusive: string | undefined }
/** What a source newly covering an interval takes of it: the interval's seconds and the share of its cover no earlier source claimed. */
interface MaintainedClaim { readonly i: number; readonly at: number; readonly spanS: number; readonly share: number }
interface MaintainedTargetStructure {
  readonly applications: ReadonlyMap<number, readonly MaintainedApplication[]>;
  readonly boundaries: readonly number[];
  readonly keyCount: number;
  /** Each interval's claims in the order the sources make them (the key), which a different magnitude can change. */
  readonly claims: Map<string, readonly (readonly MaintainedClaim[])[]>;
}
interface MaintainedKeyStructure { readonly indices: readonly number[]; readonly active: ReadonlyMap<number, number>; readonly targets: readonly MaintainedTargetStructure[] }
export interface MaintainedStructure { readonly rate: number; readonly keys: readonly MaintainedKeyStructure[] }

/**
 * The claims each interval of a target gets from the sources in the order they claim it. An application is live over an interval when one of
 * its windows spans it; the sources add to the ones before them, so each claim's share is what the cover grows by, folded in claim order.
 * None of this depends on what a source is worth, only on when and how long it runs and on the order.
 */
function claimsOf(target: MaintainedTargetStructure, order: readonly number[], indices: readonly number[], rate: number): (readonly MaintainedClaim[])[] {
  const { boundaries } = target;
  const covering = boundaries.map(() => [] as { i: number; application: MaintainedApplication }[]);
  for (const i of order) {
    for (const application of target.applications.get(i)!) {
      for (const b of application.live) covering[b]!.push({ i, application });
    }
  }
  const at = new Map(indices.map((i, position) => [i, position]));
  const stamp = new Int32Array(target.keyCount).fill(-1);
  return boundaries.map((end, b) => {
    const claims: MaintainedClaim[] = [];
    if (b === 0) return claims;
    const spanS = (end - boundaries[b - 1]!) / rate;
    // The applications live so far in this interval, with the products over them kept as they grow: a later source only adds to
    // the ones before it, in the order they were claimed, so each fold continues where the last one stopped.
    const entries: { p: number; gates?: EffectApplication['gates'] }[] = [];
    const choices = new Map<string, number>();
    let gated = false;
    let product = 1;
    let absent = 1;
    const here = covering[b]!;
    for (let k = 0; k < here.length;) {
      const i = here[k]!.i;
      for (; k < here.length && here[k]!.i === i; k++) {
        const application = here[k]!.application;
        if (stamp[application.keyId] === b) continue;
        stamp[application.keyId] = b;
        if (application.exclusive !== undefined) choices.set(application.exclusive, Math.min(1, (choices.get(application.exclusive) ?? 0) + application.p));
        else { entries.push(application); gated ||= application.gates !== undefined; product *= 1 - application.p; }
      }
      const ordinaryAbsent = gated
        ? 1 - effectApplicationTimeline([entries.map((entry) => ({ atS: 0, share: entry.p, gates: entry.gates }))], 1).activeS
        : product;
      let choiceAbsent = 1;
      for (const p of choices.values()) choiceAbsent *= 1 - p;
      const nextAbsent = ordinaryAbsent * choiceAbsent;
      claims.push({ i, at: at.get(i)!, spanS, share: absent - nextAbsent });
      absent = nextAbsent;
    }
    return claims;
  });
}

/** Cast-wide attribution reads the same sources again and again at other magnitudes: what depends on when and how long they run, not on what they are worth, is read once. */
const MAINTAINED_STRUCTURE_MIN_SOURCES = 64;
const MAINTAINED_CLAIMS_PER_TARGET = 16;

function maintainedStructureOf(sources: readonly KeyedMaintainedValue[], tickS: number | undefined, structures: Map<string, MaintainedStructure> | undefined): MaintainedStructure {
  if (structures === undefined || sources.length < MAINTAINED_STRUCTURE_MIN_SOURCES) return buildMaintainedStructure(sources, tickS);
  const signature = JSON.stringify([tickS, sources.map((source) => [source.key, source.durationS, source.tickS, source.exclusive, source.variant, source.groups])]);
  let structure = structures.get(signature);
  if (structure === undefined) {
    structure = buildMaintainedStructure(sources, tickS);
    structures.set(signature, structure);
  }
  return structure;
}

function buildMaintainedStructure(sources: readonly KeyedMaintainedValue[], tickS: number | undefined): MaintainedStructure {
  const rate = timelineRate(tickS, sources.flatMap((source) => [...applicationCoordinates(source.groups), source.durationS]));
  const unit = (seconds: number): number => Math.round(seconds * rate);
  const byKey = new Map<string, number[]>();
  sources.forEach((source, i) => byKey.set(source.key, [...(byKey.get(source.key) ?? []), i]));
  const keys: MaintainedKeyStructure[] = [];
  for (const indices of byKey.values()) {
    if (indices.length === 1) { keys.push({ indices, active: new Map(), targets: [] }); continue; }
    const lives = new Map(indices.map((i) => [i, sources[i]!.groups.map((group) => effectApplicationTimeline([group], sources[i]!.durationS, undefined, tickS))]));
    const active = new Map(indices.map((i) => [i, lives.get(i)!.reduce((sum, life) => sum + life.activeS, 0)]));
    const byTarget = new Map(indices.map((i) => [i, new Map(sources[i]!.groups.map((group, index) => [group[0]?.targetKey ?? `bucket/${index}`, group]))]));
    const targetKeys = new Set([...byTarget.values()].flatMap((groups) => [...groups.keys()]));
    const targets: MaintainedTargetStructure[] = [];
    for (const target of targetKeys) {
      const applications = new Map(indices.map((i) => [i, orderedEffectApplications(byTarget.get(i)!.get(target) ?? [], tickS).map((application, index) => ({
        key: sources[i]!.exclusive === undefined ? `${i}/${index}` : `${sources[i]!.exclusive}/${sources[i]!.variant}/${JSON.stringify(application)}`, p: Math.min(1, application.share), gates: application.gates, exclusive: sources[i]!.exclusive,
        windows: (application.offsetsS ?? [0]).map((offset) => ({ start: unit(application.atS + offset), end: unit(application.atS + offset) + (tickS === undefined ? unit(sources[i]!.durationS) : effectLifetimeTicks(sources[i]!.durationS, rate)) })),
      }))]));
      const boundaries = [...new Set([...applications.values()].flatMap((entries) => entries.flatMap((entry) => entry.windows.flatMap((window) => [window.start, window.end]))))].sort((a, b) => a - b);
      const boundaryAt = new Map(boundaries.map((boundary, at) => [boundary, at]));
      // An application is live over an interval when one of its windows spans it; applications of equal key are one cover.
      const keyIds = new Map<string, number>();
      const ready = new Map([...applications].map(([i, entries]) => [i, entries.map((entry): MaintainedApplication => {
        const live = new Set<number>();
        for (const window of entry.windows) for (let b = boundaryAt.get(window.start)! + 1; b <= boundaryAt.get(window.end)!; b++) live.add(b);
        let keyId = keyIds.get(entry.key);
        if (keyId === undefined) { keyId = keyIds.size; keyIds.set(entry.key, keyId); }
        return { keyId, live: [...live], p: entry.p, gates: entry.gates, exclusive: entry.exclusive };
      })]));
      targets.push({ applications: ready, boundaries, keyCount: keyIds.size, claims: new Map() });
    }
    keys.push({ indices, active, targets });
  }
  return { rate, keys };
}

/** One payload of a runtime DoT refresh key. Values are per emitted tick, before target population scaling. */
export interface KeyedPeriodicValue {
  readonly delayS?: number;
  readonly key: string;
  readonly source: string;
  readonly groups: EffectApplications;
  readonly durationS: number;
  readonly intervalS: number;
  readonly tickS?: number;
  readonly total: number;
}

/** Replay replacements in unit/effect order; a refresh preserves the clock and replaces the payload. */
export function keyedPeriodicValues(sources: readonly KeyedPeriodicValue[]): { total: number; ticks?: readonly { atS: number; amount: number }[] }[] {
  const values = sources.map(() => 0);
  const emitted = new Map<number, Map<number, number>>();
  const keys = new Map<string, number[]>();
  sources.forEach((source, i) => keys.set(source.key, [...(keys.get(source.key) ?? []), i]));
  for (const indices of keys.values()) {
    if (indices.length === 1) { values[indices[0]!] = sources[indices[0]!]!.total; continue; }
    for (const i of indices) emitted.set(i, new Map());
    const tickS = sources[indices[0]!]!.tickS;
    const rate = timelineRate(tickS, indices.flatMap((i) => [...applicationCoordinates(sources[i]!.groups), sources[i]!.durationS, sources[i]!.intervalS]));
    const at = (seconds: number): number => Math.round(seconds * rate);
    const durations = new Map(indices.map((i) => [i, tickS === undefined ? at(sources[i]!.durationS) : effectLifetimeTicks(sources[i]!.durationS, rate)]));
    const intervals = new Map(indices.map((i) => [i, tickS === undefined ? at(sources[i]!.intervalS) : statusIntervalTicks(sources[i]!.intervalS, rate)]));
    const rates = new Map(indices.map((i) => {
      const source = sources[i]!;
      const count = effectApplicationTimeline(source.groups, source.durationS, source.intervalS, tickS).ticks.reduce((sum, tick) => sum + tick.share, 0);
      return [i, count > 0 ? source.total / count : 0];
    }));
    const byTarget = new Map(indices.map((i) => [i, new Map(sources[i]!.groups.map((group, index) => [group[0]?.targetKey ?? `bucket/${index}`, group]))]));
    const targets = new Set([...byTarget.values()].flatMap((groups) => [...groups.keys()]));
    for (const target of targets) {
      const events = indices.filter((i) => sources[i]!.durationS > 0).flatMap((i) => orderedEffectApplications(byTarget.get(i)!.get(target) ?? [], tickS).map((application) => ({ ...application, owner: i })))
        .sort((a, b) => at(a.atS) - at(b.atS) || compareApplicationOrder(a, b));
      const emit = (state: { end: number; next: number; owner: number }, through: number, probability: number, inclusive = true): number => {
        const interval = intervals.get(state.owner) ?? 1;
        const expires = state.next <= state.end ? Math.floor((state.end - state.next) / interval) + 1 : 0;
        const before = inclusive
          ? (state.next <= through ? Math.floor((through - state.next) / interval) + 1 : 0)
          : (state.next < through ? Math.ceil((through - state.next) / interval) : 0);
        const count = Math.min(expires, before);
        if (count > 0) {
          const amount = probability * rates.get(state.owner)!;
          values[state.owner]! += count * amount;
          const ticks = emitted.get(state.owner)!;
          for (let k = 0; k < count; k++) {
            const tick = (state.next + k * interval) / rate;
            ticks.set(tick, (ticks.get(tick) ?? 0) + amount);
          }
        }
        return state.next + count * interval;
      };
      const states = runtimeRollExpectation(events.map((event) => ({ p: event.share, gates: event.gates })), { end: -Infinity, next: Infinity, owner: -1 }, (state, index, pass, probability) => {
        const event = events[index]!;
        const tick = at(event.atS);
        const next = emit(state, tick, probability, false);
        if (!pass) return { ...state, next };
        const live = state.end >= tick;
        return { end: tick + durations.get(event.owner)!, next: live ? next : tick + intervals.get(event.owner)!, owner: event.owner };
      }, (state) => `${state.end}/${state.next}/${state.owner}`);
      for (const { state, probability } of states) emit(state, state.end, probability);
    }
  }
  return values.map((total, i) => ({ total, ...(emitted.has(i) ? { ticks: [...emitted.get(i)!].sort(([a], [b]) => a - b).map(([atS, amount]) => ({ atS, amount })) } : {}) }));
}

const NO_PRIOR_ELEMENT_GROUPS: object = {};

/** Timelines owned by the runtime's refresh key for the whole cast, reused by its stage/clone walks. */
/** Whether `KeyedDotTimelines.registerEffect` records anything for this leaf: an element status, or a status that denies the monster's action. */
export function registersOnTimeline(node: LeafEffect): boolean {
  return node.kind === 'element_status' || ('durationS' in node && monsterAttackDenied([{ kind: node.kind }]));
}

export class KeyedDotTimelines {
  /** What this cast's pricing reads back instead of rebuilding; dropped with the cast. */
  readonly caches = new PricingCaches();
  private readonly timelines = new Map<string, ReturnType<typeof effectApplicationTimeline>>();
  private readonly elements = new Map<string, EffectApplications>();
  private readonly elementSources = new Map<string, ElementStatusEffect>();
  private readonly elementTimelines = new Map<string, Map<string, StagedEffectTimeline>>();
  private readonly schedules = new WeakMap<EffectApplications, string>();

  readElementTimeline(groups: EffectApplications, durationS: number, tickS: number | undefined, stageCount: number, keepSource: boolean, hostTickS?: number, delayPolicy?: PeriodicDelayPolicy): StagedEffectTimeline {
    // The schedule is read by content, once per array: a solve reads one array many times, and a cast re-registers an equal one.
    let schedule = this.schedules.get(groups);
    if (schedule === undefined) { schedule = JSON.stringify(groups); this.schedules.set(groups, schedule); }
    // Two levels, so the schedule's (long) string is looked up as the one string it was fingerprinted to, not rebuilt into a key on every read.
    let bySchedule = this.elementTimelines.get(schedule);
    if (bySchedule === undefined) { bySchedule = new Map(); this.elementTimelines.set(schedule, bySchedule); }
    const key = JSON.stringify([durationS, tickS, stageCount, keepSource, hostTickS, delayPolicy]);
    let timeline = bySchedule.get(key);
    if (timeline === undefined) {
      timeline = effectApplicationTimeline(groups, durationS, tickS, hostTickS, stageCount, keepSource, delayPolicy);
      bySchedule.set(key, timeline);
    }
    return timeline;
  }

  private readonly locks: ActionLockWindow[] = [];
  private readonly elementLockChances = new Map<string, number>();

  resetElements(): void { this.elements.clear(); this.elementSources.clear(); this.elementLockChances.clear(); this.locks.length = 0; }

  /** A hold of the cast that denies the target's actions for `durationS` from each application; shock pulses inside it land no delay. */
  registerActionLock(groups: EffectApplications, durationS: number): void {
    for (const [groupIndex, group] of groups.entries()) for (const application of group) {
      const { atS, share, offsetsS = [0] } = application;
      for (const offset of offsetsS) this.locks.push({ startS: atS + offset, endS: atS + offset + durationS, p: Math.min(1, share), rollKey: `lock:${groupIndex}/${JSON.stringify(application)}` });
    }
  }

  registerEffect(node: LeafEffect, groups: EffectApplications, susceptibility: number, tickS: number, source: string, chance = 1): void {
    if (!registersOnTimeline(node)) return;
    if (node.kind === 'element_status') {
      const spec = requireElementPackage(node.name);
      const durationS = statusDurationTicks(elementLifetimeS(spec, spec.initialStage, elementLeafProvenance(node), susceptibility), 1 / tickS) * tickS;
      this.registerElement(node, groups, durationS, source);
      this.elementLockChances.set(source, chance);
    } else if ('durationS' in node) {
      this.registerActionLock(groups.map((group) => group.map((entry) => ({ ...entry, share: entry.share * chance }))), statusDurationTicks(node.durationS * susceptibility, 1 / tickS) * tickS);
    }
  }

  actionLocks(susceptibility: number, tickS: number): readonly ActionLockWindow[] {
    const locks = [...this.locks];
    for (const [name, groups] of this.elements) {
      const spec = requireElementPackage(name);
      const first = groups.flat()[0];
      if (first === undefined) continue;
      const timeline = this.readElementTimeline(groups, first.durationS!, undefined, spec.stageByStacks === true ? spec.stages.length : 1, spec.sameCast === 'REFRESH_KEEP', tickS);
      for (const span of timeline.spans) {
        const node = this.elementSource(span.source!);
        for (const { leaf, weight, depth } of elementPricedLeaves(name, elementLeafProvenance(node), 1 / tickS, spec.stages[span.stacks! - 1]!.id, susceptibility)) {
          if (depth !== 0 || !('durationS' in leaf) || !monsterAttackDenied([{ kind: leaf.kind }])) continue;
          locks.push({ startS: span.startTick, endS: span.endTick, p: span.probability * weight * (this.elementLockChances.get(span.source!) ?? 1) });
        }
      }
    }
    return locks;
  }

  // A cast registers the same statuses on the same schedules again for every magnitude it prices, so what registering builds is built once per input,
  // and the schedules a read fingerprints are the same arrays, not equal copies.
  private readonly registeredGroups = new WeakMap<EffectApplications, Map<string, EffectApplications>>();
  private readonly mergedGroups = new WeakMap<object, WeakMap<EffectApplications, EffectApplications>>();

  registerElement(node: ElementStatusEffect, groups: EffectApplications, durationS: number, source: string): void {
    const name = node.name;
    this.elementSources.set(source, node);
    if (!(node.potency > 0)) return;
    let byPlace = this.registeredGroups.get(groups);
    if (byPlace === undefined) { byPlace = new Map(); this.registeredGroups.set(groups, byPlace); }
    const placeKey = JSON.stringify([source, durationS]);
    let placed = byPlace.get(placeKey);
    if (placed === undefined) { placed = groups.map((group) => group.map((entry) => ({ ...entry, source, durationS }))); byPlace.set(placeKey, placed); }
    const prior = this.elements.get(name);
    let byPlaced = this.mergedGroups.get(prior ?? NO_PRIOR_ELEMENT_GROUPS);
    if (byPlaced === undefined) { byPlaced = new WeakMap(); this.mergedGroups.set(prior ?? NO_PRIOR_ELEMENT_GROUPS, byPlaced); }
    let merged = byPlaced.get(placed);
    if (merged === undefined) {
      const before = prior ?? [];
      merged = Array.from({ length: Math.max(before.length, placed.length) }, (_, i) => [...(before[i] ?? []), ...(placed![i] ?? [])]);
      byPlaced.set(placed, merged);
    }
    this.elements.set(name, merged);
  }

  hasElementSource(source: string | undefined): boolean { return source !== undefined && this.elementSources.has(source); }

  elementSource(source: string): ElementStatusEffect { return this.elementSources.get(source)!; }

  elementGroups(name: string): EffectApplications | undefined { return this.elements.get(name); }

  read(node: { readonly kind: 'dot'; readonly damageType?: string; readonly tag?: string; readonly durationS: number; readonly tickS: number }, stageId: string | undefined, groups: EffectApplications, hostTickS?: number): ReturnType<typeof effectApplicationTimeline> {
    const key = `${pricedDotKey(node, stageId)}|${hostTickS}|${JSON.stringify(groups)}`;
    let timeline = this.timelines.get(key);
    if (timeline === undefined) {
      timeline = effectApplicationTimeline(groups, node.durationS, node.tickS, hostTickS);
      this.timelines.set(key, timeline);
    }
    return timeline;
  }
}
